import React, { useState, useEffect, useMemo } from 'react';
import { Spin, Tag, Empty } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { getAllOffDates, getActiveEmployees } from '../../api/supabase';
import { colors, spacing, radius, typography, transitions } from '../../styles/monochromeTheme';

dayjs.locale('fr');

const OffOverview = () => {
  const [offDates, setOffDates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'OFF', 'ASTREINTE', 'BASE'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [offDatesData, employeesData] = await Promise.all([
        getAllOffDates(),
        getActiveEmployees()
      ]);
      setOffDates(offDatesData.data || []);
      setEmployees(employeesData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get employee name by ID
  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    if (!emp) return 'Unknown';
    const firstName = emp.first_name || '';
    const lastName = emp.name || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown';
  };

  // Filter and sort entries
  const filteredEntries = useMemo(() => {
    let entries = [...offDates];

    // Filter by category
    if (filter === 'OFF') {
      entries = entries.filter(e => e.category === 'OFF');
    } else if (filter === 'ASTREINTE') {
      entries = entries.filter(e => e.category === 'ASTREINTE');
    } else if (filter === 'BASE') {
      entries = entries.filter(e => e.category === 'BASE' || e.category === 'BASE_AM' || e.category === 'BASE_PM');
    }

    // Sort by date descending (most recent first)
    entries.sort((a, b) => dayjs(b.off_date).unix() - dayjs(a.off_date).unix());

    return entries.slice(0, 50); // Show last 50 entries
  }, [offDates, filter]);

  // Group entries by date
  const groupedByDate = useMemo(() => {
    const groups = {};
    filteredEntries.forEach(entry => {
      const dateKey = entry.off_date;
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(entry);
    });
    return groups;
  }, [filteredEntries]);

  const getCategoryStyle = (category) => {
    switch (category) {
      case 'OFF':
        return { bg: '#f6ffed', color: '#389e0d', border: '#b7eb8f' };
      case 'ASTREINTE':
        return { bg: '#fff7e6', color: '#d46b08', border: '#ffd591' };
      case 'BASE':
        return { bg: '#e6fffb', color: '#08979c', border: '#87e8de' };
      case 'BASE_AM':
        return { bg: '#e6f7ff', color: '#096dd9', border: '#91d5ff' };
      case 'BASE_PM':
        return { bg: '#f0f5ff', color: '#2f54eb', border: '#adc6ff' };
      default:
        return { bg: '#f5f5f5', color: '#666', border: '#d9d9d9' };
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'OFF': return '🏖️';
      case 'ASTREINTE': return '🚨';
      case 'BASE': return '🏢';
      case 'BASE_AM': return '🌅';
      case 'BASE_PM': return '🌇';
      default: return '📅';
    }
  };

  const formatDate = (dateStr) => {
    const date = dayjs(dateStr);
    const today = dayjs().startOf('day');
    const diff = date.diff(today, 'day');

    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return 'Demain';
    if (diff === -1) return 'Hier';
    if (diff > 1 && diff <= 7) return `Dans ${diff} jours`;
    if (diff < -1 && diff >= -7) return `Il y a ${Math.abs(diff)} jours`;

    return date.format('ddd D MMM');
  };

  const filterBtnStyle = (active) => ({
    padding: `${spacing.sm} ${spacing.lg}`,
    borderRadius: radius.lg,
    border: active ? 'none' : `1px solid ${colors.borderMedium}`,
    background: active ? colors.bgDark : colors.bgSecondary,
    color: active ? colors.textInverse : colors.textSecondary,
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.sm,
    cursor: 'pointer',
    transition: transitions.fast
  });

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bgPrimary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bgPrimary,
      padding: spacing.xl
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: spacing.xl,
          paddingBottom: spacing.lg,
          borderBottom: `1px solid ${colors.borderLight}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: colors.bgDark,
              borderRadius: radius.xl,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px'
            }}>
              <ClockCircleOutlined style={{ color: colors.textInverse }} />
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: typography.fontSize.xl,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary
              }}>
                Aperçu Rapide
              </h1>
              <p style={{
                margin: 0,
                fontSize: typography.fontSize.sm,
                color: colors.textTertiary
              }}>
                Derniers OFF & Astreintes
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          <div style={{ display: 'flex', gap: spacing.md }}>
            <div style={{
              padding: `${spacing.sm} ${spacing.md}`,
              background: '#f6ffed',
              borderRadius: radius.lg,
              border: '1px solid #b7eb8f'
            }}>
              <span style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: '#389e0d' }}>
                {offDates.filter(e => e.category === 'OFF').length}
              </span>
              <span style={{ fontSize: typography.fontSize.xs, color: '#389e0d', marginLeft: spacing.xs }}>OFF</span>
            </div>
            <div style={{
              padding: `${spacing.sm} ${spacing.md}`,
              background: '#fff7e6',
              borderRadius: radius.lg,
              border: '1px solid #ffd591'
            }}>
              <span style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: '#d46b08' }}>
                {offDates.filter(e => e.category === 'ASTREINTE').length}
              </span>
              <span style={{ fontSize: typography.fontSize.xs, color: '#d46b08', marginLeft: spacing.xs }}>Astreintes</span>
            </div>
          </div>
        </div>

        {/* Filter Buttons */}
        <div style={{
          display: 'flex',
          gap: spacing.sm,
          marginBottom: spacing.xl
        }}>
          <button onClick={() => setFilter('all')} style={filterBtnStyle(filter === 'all')}>
            Tout
          </button>
          <button onClick={() => setFilter('OFF')} style={filterBtnStyle(filter === 'OFF')}>
            🏖️ OFF
          </button>
          <button onClick={() => setFilter('ASTREINTE')} style={filterBtnStyle(filter === 'ASTREINTE')}>
            🚨 Astreintes
          </button>
          <button onClick={() => setFilter('BASE')} style={filterBtnStyle(filter === 'BASE')}>
            🏢 Base
          </button>
        </div>

        {/* Entries List */}
        {Object.keys(groupedByDate).length === 0 ? (
          <Empty description="Aucune entrée" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
            {Object.entries(groupedByDate).map(([date, entries]) => (
              <div key={date} style={{
                background: colors.bgSecondary,
                borderRadius: radius.xl,
                padding: spacing.lg,
                border: `1px solid ${colors.borderLight}`
              }}>
                {/* Date Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: spacing.md,
                  paddingBottom: spacing.sm,
                  borderBottom: `1px solid ${colors.borderLight}`
                }}>
                  <div style={{
                    fontSize: typography.fontSize.md,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textPrimary
                  }}>
                    {dayjs(date).format('dddd D MMMM YYYY')}
                  </div>
                  <div style={{
                    fontSize: typography.fontSize.xs,
                    color: colors.textTertiary,
                    background: colors.bgTertiary,
                    padding: `${spacing.xs} ${spacing.sm}`,
                    borderRadius: radius.md
                  }}>
                    {formatDate(date)}
                  </div>
                </div>

                {/* Entries for this date */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: spacing.sm
                }}>
                  {entries.map(entry => {
                    const style = getCategoryStyle(entry.category);
                    return (
                      <div key={entry.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: spacing.sm,
                        padding: spacing.sm,
                        background: style.bg,
                        borderRadius: radius.md,
                        border: `1px solid ${style.border}`
                      }}>
                        <span style={{ fontSize: '16px' }}>{getCategoryIcon(entry.category)}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: typography.fontSize.sm,
                            fontWeight: typography.fontWeight.semibold,
                            color: style.color,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {getEmployeeName(entry.employee_id)}
                          </div>
                          <div style={{
                            fontSize: typography.fontSize.xs,
                            color: style.color,
                            opacity: 0.8
                          }}>
                            {entry.category.replace('_', ' ')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OffOverview;
